/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDesignTokens } from '@hooks/useDesignTokens'
import { TOKENS } from '@gen/tokenConstants'
import { CalendarType } from '@/types/calendar'

interface CalendarFormData {
  name: string
  description: string
  type: CalendarType
}

export default function CalendarAddPage() {
  const { getValue } = useDesignTokens()
  const navigate = useNavigate()
  const [formData, setFormData] = useState<CalendarFormData>({
    name: '',
    description: '',
    type: 'room',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    console.log('Creating calendar:', formData)
    navigate('/')
  }

  function handleInputChange(
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  return (
    <div
      css={{
        minHeight: '100vh',
        backgroundColor: getValue(TOKENS.color.neutral['50']),
        padding: getValue(TOKENS.spacing.lg),
      }}
    >
      <div
        css={{
          maxWidth: '600px',
          margin: '0 auto',
          backgroundColor: '#ffffff',
          padding: getValue(TOKENS.spacing.xl),
          borderRadius: getValue(TOKENS.borderRadius.md),
          boxShadow: `0 2px 8px ${getValue(TOKENS.color.neutral['200'])}`,
        }}
      >
        <h1
          css={{
            fontSize: getValue(TOKENS.typography.fontSize.h2),
            fontWeight: getValue(TOKENS.typography.fontWeight.semibold),
            color: getValue(TOKENS.color.neutral['900']),
            marginBottom: getValue(TOKENS.spacing.lg),
            margin: 0,
          }}
        >
          Add New Calendar
        </h1>

        <form onSubmit={handleSubmit}>
          <div
            css={{
              marginBottom: getValue(TOKENS.spacing.lg),
            }}
          >
            <label
              htmlFor='name'
              css={{
                display: 'block',
                fontSize: getValue(TOKENS.typography.fontSize.md),
                fontWeight: getValue(TOKENS.typography.fontWeight.medium),
                color: getValue(TOKENS.color.neutral['700']),
                marginBottom: getValue(TOKENS.spacing.sm),
              }}
            >
              Calendar Name *
            </label>
            <input
              type='text'
              id='name'
              name='name'
              value={formData.name}
              onChange={handleInputChange}
              required
              css={{
                width: '100%',
                padding: getValue(TOKENS.spacing.md),
                border: `1px solid ${getValue(TOKENS.color.neutral['300'])}`,
                borderRadius: getValue(TOKENS.borderRadius.sm),
                fontSize: getValue(TOKENS.typography.fontSize.md),
                fontFamily: getValue(TOKENS.typography.fontFamily.primary),
                '&:focus': {
                  outline: 'none',
                  borderColor: getValue(TOKENS.color.primary['500']),
                  boxShadow: `0 0 0 3px ${getValue(TOKENS.color.primary['100'])}`,
                },
              }}
            />
          </div>

          <div
            css={{
              marginBottom: getValue(TOKENS.spacing.lg),
            }}
          >
            <label
              htmlFor='description'
              css={{
                display: 'block',
                fontSize: getValue(TOKENS.typography.fontSize.md),
                fontWeight: getValue(TOKENS.typography.fontWeight.medium),
                color: getValue(TOKENS.color.neutral['700']),
                marginBottom: getValue(TOKENS.spacing.sm),
              }}
            >
              Description
            </label>
            <textarea
              id='description'
              name='description'
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              css={{
                width: '100%',
                padding: getValue(TOKENS.spacing.md),
                border: `1px solid ${getValue(TOKENS.color.neutral['300'])}`,
                borderRadius: getValue(TOKENS.borderRadius.sm),
                fontSize: getValue(TOKENS.typography.fontSize.md),
                fontFamily: getValue(TOKENS.typography.fontFamily.primary),
                resize: 'vertical',
                '&:focus': {
                  outline: 'none',
                  borderColor: getValue(TOKENS.color.primary['500']),
                  boxShadow: `0 0 0 3px ${getValue(TOKENS.color.primary['100'])}`,
                },
              }}
            />
          </div>

          <div
            css={{
              marginBottom: getValue(TOKENS.spacing.xl),
            }}
          >
            <label
              htmlFor='type'
              css={{
                display: 'block',
                fontSize: getValue(TOKENS.typography.fontSize.md),
                fontWeight: getValue(TOKENS.typography.fontWeight.medium),
                color: getValue(TOKENS.color.neutral['700']),
                marginBottom: getValue(TOKENS.spacing.sm),
              }}
            >
              Calendar Type *
            </label>
            <select
              id='type'
              name='type'
              value={formData.type}
              onChange={handleInputChange}
              required
              css={{
                width: '100%',
                padding: getValue(TOKENS.spacing.md),
                border: `1px solid ${getValue(TOKENS.color.neutral['300'])}`,
                borderRadius: getValue(TOKENS.borderRadius.sm),
                fontSize: getValue(TOKENS.typography.fontSize.md),
                fontFamily: getValue(TOKENS.typography.fontFamily.primary),
                backgroundColor: '#ffffff',
                '&:focus': {
                  outline: 'none',
                  borderColor: getValue(TOKENS.color.primary['500']),
                  boxShadow: `0 0 0 3px ${getValue(TOKENS.color.primary['100'])}`,
                },
              }}
            >
              <option value='room'>Room</option>
              <option value='asset'>Asset</option>
              <option value='other'>Other</option>
            </select>
          </div>

          <div
            css={{
              display: 'flex',
              gap: getValue(TOKENS.spacing.md),
              justifyContent: 'flex-end',
            }}
          >
            <button
              type='button'
              onClick={() => navigate('/')}
              css={{
                padding: `${getValue(TOKENS.spacing.md)} ${getValue(TOKENS.spacing.lg)}`,
                border: `1px solid ${getValue(TOKENS.color.neutral['300'])}`,
                borderRadius: getValue(TOKENS.borderRadius.sm),
                fontSize: getValue(TOKENS.typography.fontSize.md),
                fontFamily: getValue(TOKENS.typography.fontFamily.primary),
                backgroundColor: '#ffffff',
                color: getValue(TOKENS.color.neutral['700']),
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: getValue(TOKENS.color.neutral['50']),
                },
                '&:focus': {
                  outline: 'none',
                  boxShadow: `0 0 0 3px ${getValue(TOKENS.color.neutral['200'])}`,
                },
              }}
            >
              Cancel
            </button>
            <button
              type='submit'
              css={{
                padding: `${getValue(TOKENS.spacing.md)} ${getValue(TOKENS.spacing.lg)}`,
                border: 'none',
                borderRadius: getValue(TOKENS.borderRadius.sm),
                fontSize: getValue(TOKENS.typography.fontSize.md),
                fontFamily: getValue(TOKENS.typography.fontFamily.primary),
                backgroundColor: getValue(TOKENS.color.primary['500']),
                color: '#ffffff',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: getValue(TOKENS.color.primary['600']),
                },
                '&:focus': {
                  outline: 'none',
                  boxShadow: `0 0 0 3px ${getValue(TOKENS.color.primary['100'])}`,
                },
                '&:disabled': {
                  backgroundColor: getValue(TOKENS.color.neutral['300']),
                  cursor: 'not-allowed',
                },
              }}
              disabled={!formData.name.trim()}
            >
              Create Calendar
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
